import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import api from '../../services/api';
import Layout from '../common/Layout';
import ViewLead from '../leads/ViewLead';

/* ============================================================
   DATE / TIME HELPERS
   ============================================================ */

function parseSafeDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : value;
  }

  let text = String(value).trim();

  if (!text) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [
      year,
      month,
      day
    ] = text.split('-').map(Number);

    const date = new Date(
      year,
      month - 1,
      day
    );

    return Number.isNaN(date.getTime())
      ? null
      : date;
  }

  text = text.replace(
    /(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/,
    '$1$2'
  );

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function formatDisplayDate(value) {
  if (!value) {
    return 'N/A';
  }

  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
  ) {
    const [
      year,
      month,
      day
    ] = value.trim().split('-').map(Number);

    const date = new Date(
      year,
      month - 1,
      day
    );

    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleDateString(
      'en-US',
      {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    );
  }

  const date = parseSafeDate(value);

  if (!date) {
    return 'N/A';
  }

  return date.toLocaleDateString(
    'en-US',
    {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }
  );
}

function formatDisplayTime(value) {
  if (!value) {
    return 'N/A';
  }

  const text = String(value).trim();

  if (!text) {
    return 'N/A';
  }

  if (
    /^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i.test(text)
  ) {
    return text;
  }

  const plainTimeMatch = text.match(
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );

  if (plainTimeMatch) {
    const hours = Number(
      plainTimeMatch[1]
    );

    const minutes = Number(
      plainTimeMatch[2]
    );

    if (
      hours >= 0 &&
      hours <= 23 &&
      minutes >= 0 &&
      minutes <= 59
    ) {
      const date = new Date(
        2000,
        0,
        1,
        hours,
        minutes,
        0
      );

      return date.toLocaleTimeString(
        'en-US',
        {
          hour: 'numeric',
          minute: '2-digit'
        }
      );
    }
  }

  const parsed = parseSafeDate(text);

  if (!parsed) {
    return 'N/A';
  }

  return parsed.toLocaleTimeString(
    'en-US',
    {
      hour: 'numeric',
      minute: '2-digit'
    }
  );
}

function getTimePart(value) {
  if (!value) {
    return '';
  }

  const text = String(value).trim();

  const isoMatch = text.match(
    /T(\d{2}):(\d{2})(?::\d{2})?/
  );

  if (isoMatch) {
    return `${isoMatch[1]}:${isoMatch[2]}`;
  }

  const timeMatch = text.match(
    /^(\d{1,2}):(\d{2})/
  );

  if (timeMatch) {
    return `${String(
      Number(timeMatch[1])
    ).padStart(2, '0')}:${timeMatch[2]}`;
  }

  return text;
}

/* ============================================================
   TODAY / DATE
   ============================================================ */

function getTodayString() {
  const now = new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1
    ).padStart(2, '0');

  const day =
    String(
      now.getDate()
    ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function addDays(
  dateString,
  days
) {
  const date = new Date(
    `${dateString}T00:00:00`
  );

  date.setDate(
    date.getDate() + days
  );

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, '0');

  const day =
    String(
      date.getDate()
    ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/* ============================================================
   DEMO STATUS
   ============================================================ */

function DemoStatus({
  status,
  date,
  time
}) {
  const map = {
    Scheduled:
      'demo-status-scheduled',

    Completed:
      'demo-status-completed',

    Cancelled:
      'demo-status-cancelled',

    Rescheduled:
      'demo-status-rescheduled',

    'Not Booked':
      'demo-status-not-booked'
  };

  const className =
    map[status] ||
    'demo-status-not-booked';

  return (
    <div className="demo-status">
      <span className={className}>
        {status || 'Not Booked'}
      </span>

      {date && (
        <span className="demo-date-time">
          {date}

          {time &&
            ` at ${time}`}
        </span>
      )}
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

function BookDemo() {
  const {
    id
  } = useParams();

  const navigate =
    useNavigate();

  const [
    lead,
    setLead
  ] = useState(null);

  const [
    loadingLead,
    setLoadingLead
  ] = useState(true);

  const [
    loadingSlots,
    setLoadingSlots
  ] = useState(false);

  const [
    booking,
    setBooking
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState(null);

  const [showViewLead, setShowViewLead] = useState(false);

  const today =
    useMemo(
      () => getTodayString(),
      []
    );

  const [
    selectedDate,
    setSelectedDate
  ] = useState(today);

  const [
    availability,
    setAvailability
  ] = useState(null);

  const [
    services,
    setServices
  ] = useState([]);

  const [
    selectedServiceId,
    setSelectedServiceId
  ] = useState('');

  const [
    selectedContactId,
    setSelectedContactId
  ] = useState('');

  const [
    selectedSlot,
    setSelectedSlot
  ] = useState(null);

  const [
    notes,
    setNotes
  ] = useState('');

  /* ==========================================================
     LOAD LEAD
     ========================================================== */

  useEffect(() => {
    let mounted = true;

    const loadLead = async () => {
      setLoadingLead(true);
      setError('');

      try {
        const data =
          await api.getLead(id);

        if (mounted) {
          setLead(data);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err?.response?.data?.error ||
            'Failed to load lead'
          );
        }
      } finally {
        if (mounted) {
          setLoadingLead(false);
        }
      }
    };

    loadLead();

    return () => {
      mounted = false;
    };
  }, [id]);

  /* ==========================================================
     LOAD MICROSOFT SERVICES
     ========================================================== */

  useEffect(() => {
    let mounted = true;

    const loadServices = async () => {
      try {
        const data = await api.getMicrosoftBookingServices();
        const list = Array.isArray(data?.services) ? data.services : [];
        if (mounted) {
          setServices(list);
          if (!selectedServiceId && list.length) {
            setSelectedServiceId(String(list[0].id));
          }
        }
      } catch (err) {
        console.error('Failed to load Microsoft services:', err);
        if (mounted) {
          setError(err?.response?.data?.error || 'Failed to load Microsoft Bookings services');
        }
      }
    };

    loadServices();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const contacts = Array.isArray(lead?.contacts) ? lead.contacts : [];
    if (contacts.length && !selectedContactId) {
      setSelectedContactId(String(contacts[0].id || contacts[0].contact_id || ''));
    }
  }, [lead, selectedContactId]);

  /* ==========================================================
     LOAD MICROSOFT AVAILABILITY
     ========================================================== */

  useEffect(() => {
    let mounted = true;

    const loadAvailability = async () => {
      if (!selectedDate || !selectedServiceId) {
        return;
      }

      setLoadingSlots(true);

      setSelectedSlot(null);

      try {
        const data =
          await api.getMicrosoftBookingAvailability(
            selectedDate,
            selectedServiceId
          );

        if (mounted) {
          setAvailability(data);

          /*
           * Do not clear a lead error here unless
           * availability itself succeeded.
           */
          setError('');
        }
      } catch (err) {
        console.error(
          'Failed to load Microsoft availability:',
          err
        );

        if (mounted) {
          setAvailability(null);

          setError(
            err?.response?.data?.error ||
            'Failed to load Microsoft Bookings availability'
          );
        }
      } finally {
        if (mounted) {
          setLoadingSlots(false);
        }
      }
    };

    loadAvailability();

    return () => {
      mounted = false;
    };
  }, [selectedDate, selectedServiceId]);

  /* ==========================================================
     DATE OPTIONS
     ========================================================== */

  const dateOptions =
    useMemo(() => {
      return Array.from(
        {
          length: 14
        },
        (_, index) =>
          addDays(
            today,
            index
          )
      );
    }, [today]);

  /* ==========================================================
     SLOTS
     ========================================================== */

  const slots =
    Array.isArray(
      availability?.slots
    )
      ? availability.slots
      : [];

  /* ==========================================================
     BOOK DEMO
     ========================================================== */

  const handleBooking =
    async () => {
      if (!selectedSlot) {
        setError(
          'Please select a time.'
        );

        return;
      }

      if (!lead) {
        setError(
          'Lead information is not available.'
        );

        return;
      }

      /*
       * IMPORTANT:
       *
       * The selected slot is returned by the backend.
       *
       * Backend availability contains:
       *
       * startUtc
       * endUtc
       * userStart
       * userEnd
       *
       * For booking validation we send the USER LOCAL
       * slot values because the backend explicitly matches
       * these against availability.userStart/userEnd.
       *
       * Do NOT convert these values with new Date().
       */

      const bookingStart =
        selectedSlot.userStart ||
        selectedSlot.start;

      const bookingEnd =
        selectedSlot.userEnd ||
        selectedSlot.end;

      if (
        !bookingStart ||
        !bookingEnd
      ) {
        setError(
          'Selected booking slot is invalid. Please select another time.'
        );

        return;
      }

      setBooking(true);

      setError('');
      setSuccess(null);

      try {
        /*
         * IMPORTANT:
         *
         * Send the exact backend availability values.
         *
         * Do NOT use:
         *
         * new Date(bookingStart).toISOString()
         *
         * because that can change the intended local
         * appointment time before backend validation.
         */
        const result =
          await api.bookMicrosoftDemo({
            leadId:
              Number(id),

            serviceId:
              selectedServiceId,

            contactId:
              selectedContactId || undefined,

            start:
              bookingStart,

            end:
              bookingEnd,

            notes:
              notes.trim()
          });

        /*
         * Backend returns the complete booking response:
         *
         * bookingId
         * appointmentId
         * joinWebUrl
         * demoUtc
         * demoLocal
         * userTimeZone
         * microsoftTimeZone
         * lead
         */
        setSuccess(result);

      } catch (err) {
        console.error(
          'Microsoft booking failed:',
          err
        );

        setError(
          err?.response?.data?.error ||
          'Failed to book demo'
        );
      } finally {
        setBooking(false);
      }
    };

  const handleViewLead = () => {
    console.log('📋 Opening ViewLead overlay for lead:', id);
    setShowViewLead(true);
  };

  const handleCloseViewLead = () => {
    console.log('📋 Closing ViewLead overlay');
    setShowViewLead(false);
  };

  const handleViewLeadChanged = async () => {
    // Refresh lead data if changes were made
    console.log('🔄 Lead updated, refreshing...');
    try {
      const data = await api.getLead(id);
      if (data) {
        setLead(data);
      }
    } catch (err) {
      console.error('Failed to refresh lead:', err);
    }
  };

  /* ==========================================================
     LOADING LEAD
     ========================================================== */

  if (loadingLead) {
    return (
      <Layout
        title="Book Demo"
        subtitle="Microsoft Bookings"
      >
        <div className="loading-spinner">
          <div className="spinner"></div>

          <div>
            Loading lead...
          </div>
        </div>
      </Layout>
    );
  }

  /* ==========================================================
     LEAD NOT FOUND
     ========================================================== */

  if (!lead) {
    return (
      <Layout
        title="Book Demo"
        subtitle="Microsoft Bookings"
      >
        <div className="error-container">

          <div className="error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>

          <div className="error-message">
            {error || 'Lead not found'}
          </div>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              navigate('/leads')
            }
          >
            Back to Leads
          </button>

        </div>
      </Layout>
    );
  }

  /* ==========================================================
     SUCCESS / CONFIRMATION
     ========================================================== */

  if (success) {
    const bookedLead =
      success.lead ||
      success.bookedLead ||
      null;

    const appointment =
      success.appointment ||
      success.booking ||
      success.microsoftAppointment ||
      null;

    /*
     * Backend now returns demoLocal.
     *
     * Prefer that over converting UTC again.
     */
    const localStart =
      success.demoLocal?.start ||
      selectedSlot?.userStart ||
      selectedSlot?.start ||
      selectedDate;

    const bookedDate =
      formatDisplayDate(
        localStart
      );

    const bookedTime =
      formatDisplayTime(
        localStart
      );

    const bookingId =
      success.bookingId ||
      success.microsoftBookingId ||
      success.microsoft_booking_id ||
      appointment?.id ||
      appointment?.selfServiceAppointmentId ||
      '';

    const joinWebUrl =
      success.joinWebUrl ||
      success.join_url ||
      success.microsoftJoinUrl ||
      appointment?.joinWebUrl ||
      appointment?.anonymousJoinWebUrl ||
      '';

    return (
      <>
        <Layout
          title="Demo Scheduled"
          subtitle="Microsoft Bookings confirmation"
        >
          <div className="card">

            <div
              style={{
                textAlign: 'center',
                padding: '30px 20px'
              }}
            >

              <div
                className="success-icon"
                style={{
                  marginBottom: '15px'
                }}
              >
                <i className="fas fa-check-circle"></i>
              </div>

              <h2
                style={{
                  marginBottom: '8px'
                }}
              >
                Demo Scheduled
              </h2>

              <p
                className="text-muted"
                style={{
                  marginBottom: '22px'
                }}
              >
                Microsoft Bookings appointment
                created successfully.
              </p>

              <div
                style={{
                  background: '#f7f9fb',
                  border: '1px solid #e2e8ee',
                  borderRadius: '8px',
                  padding: '18px',
                  maxWidth: '600px',
                  margin: '0 auto 20px'
                }}
              >

                <strong>
                  {bookedLead?.company ||
                    lead.company ||
                    'Customer'}
                </strong>

                <div
                  style={{
                    marginTop: '10px'
                  }}
                >
                  {bookedDate}
                </div>

                <div>
                  {bookedTime}
                </div>

                <div
                  style={{
                    marginTop: '10px'
                  }}
                >
                  <DemoStatus
                    status="Scheduled"
                    date={
                      bookedDate !== 'N/A'
                        ? bookedDate
                        : ''
                    }
                    time={
                      bookedTime !== 'N/A'
                        ? bookedTime
                        : ''
                    }
                  />
                </div>

              </div>

              {success.userTimeZone && (
                <div
                  className="text-muted"
                  style={{
                    fontSize: '.8rem',
                    marginBottom: '15px'
                  }}
                >
                  Time zone: {success.userTimeZone}
                </div>
              )}

              {joinWebUrl && (
                <div
                  style={{
                    marginBottom: '20px'
                  }}
                >
                  <a
                    href={joinWebUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success"
                  >
                    <i className="fas fa-video"></i>
                    {' '}
                    Join Teams Meeting
                  </a>
                </div>
              )}

              {bookingId && (
                <div
                  style={{
                    fontSize: '.75rem',
                    color: '#7c8798',
                    marginBottom: '20px',
                    wordBreak: 'break-all'
                  }}
                >
                  <strong>
                    Microsoft Booking ID:
                  </strong>

                  <br />

                  {bookingId}
                </div>
              )}

              <div className="form-actions">

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() =>
                    navigate('/leads')
                  }
                >
                  Back to My Leads
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleViewLead}
                >
                  View Lead
                </button>

              </div>

            </div>

          </div>
        </Layout>

        {/* ViewLead Overlay - rendered outside Layout to cover full screen */}
        {showViewLead && (
          <ViewLead
            leadId={Number(id)}
            embedded={true}
            onClose={handleCloseViewLead}
            onChanged={handleViewLeadChanged}
          />
        )}
      </>
    );
  }

  const contacts = Array.isArray(lead.contacts) ? lead.contacts : [];
  const selectedContact =
    contacts.find(contact => String(contact.id || contact.contact_id) === String(selectedContactId)) ||
    contacts[0] || null;

  const selectedService =
    services.find(service => String(service.id) === String(selectedServiceId)) || null;

  const calendarSlots = [
    ...(Array.isArray(availability?.slots) ? availability.slots.map(slot => ({ ...slot, status: 'available' })) : []),
    ...(Array.isArray(availability?.busySlots) ? availability.busySlots.map(slot => ({ ...slot, status: 'busy' })) : [])
  ].sort((a, b) => String(a.startUtc || '').localeCompare(String(b.startUtc || '')));

  /* ==========================================================
     BOOKING SCREEN
     ========================================================== */

  return (
    <Layout
      title="Book Demo"
      subtitle={`Schedule Microsoft demo for ${lead.company}`}
    >

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {' '}
          {error}
        </div>
      )}

      <div className="lead-details-grid">

        {/* ==================================================
            CUSTOMER INFORMATION
            ================================================== */}

        <div className="card detail-card">

          <h3>
            Customer Information
          </h3>

          <div className="detail-row">

            <span className="label">
              Dealership
            </span>

            <span className="value">
              {lead.company || 'N/A'}
            </span>

          </div>

          <div className="detail-row">

            <span className="label">
              Email
            </span>

            <span className="value">
              {selectedContact?.email || lead.email || 'N/A'}
            </span>

          </div>

          <div className="detail-row">

            <span className="label">
              Contact
            </span>

            <span className="value">
              {selectedContact?.name || [selectedContact?.first_name || selectedContact?.firstName, selectedContact?.last_name || selectedContact?.lastName].filter(Boolean).join(' ') || 'N/A'}
            </span>

          </div>

          <div className="detail-row">

            <span className="label">
              Phone
            </span>

            <span className="value">
              {selectedContact?.work_phone || selectedContact?.mobile_phone || selectedContact?.workPhone || selectedContact?.mobilePhone || lead.main_line || 'N/A'}
            </span>

          </div>

        </div>

        {/* ==================================================
            BOOKING
            ================================================== */}

        <div className="card detail-card">

          <h3>
            Book Microsoft Demo
          </h3>

          <div className="form-group">
            <label>Microsoft Bookings Service *</label>
            <select
              className="form-control"
              value={selectedServiceId}
              onChange={(event) => {
                setSelectedServiceId(event.target.value);
                setSelectedSlot(null);
                setAvailability(null);
              }}
              disabled={booking || !services.length}
            >
              <option value="">Select service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Assigned Staff</label>
            <input
              className="form-control"
              readOnly
              value={(selectedService?.assignedStaff || availability?.assignedStaff || []).map(staff => staff.displayName).join(', ') || 'Assigned by Microsoft Bookings'}
            />
            <small className="text-muted">Staff is controlled by Microsoft Bookings and cannot be changed here.</small>
          </div>

          <div className="form-group">
            <label>Customer Contact *</label>
            <select
              className="form-control"
              value={selectedContactId}
              onChange={(event) => setSelectedContactId(event.target.value)}
              disabled={booking || !contacts.length}
            >
              {contacts.length ? contacts.map((contact, index) => (
                <option key={contact.id || contact.contact_id || index} value={contact.id || contact.contact_id || ''}>
                  {contact.name || [contact.first_name || contact.firstName, contact.last_name || contact.lastName].filter(Boolean).join(' ') || `Contact ${index + 1}`}
                </option>
              )) : <option value="">Primary lead contact</option>}
            </select>
          </div>

          <div className="form-group">

            <label>
              Demo Date
            </label>

            <select
              className="form-control"
              value={selectedDate}
              onChange={(event) =>
                setSelectedDate(
                  event.target.value
                )
              }
              disabled={booking}
            >

              {dateOptions.map(
                date => (
                  <option
                    key={date}
                    value={date}
                  >
                    {formatDisplayDate(date)}
                  </option>
                )
              )}

            </select>

          </div>

          <div className="form-group">

            <label>
              Available Times
            </label>

            {loadingSlots ? (

              <div
                className="loading-spinner"
                style={{
                  minHeight: '120px',
                  padding: '20px'
                }}
              >
                <div className="spinner"></div>

                <div>
                  Loading Microsoft availability...
                </div>
              </div>

            ) : calendarSlots.length === 0 ? (

              <div className="empty-state">

                <i className="fas fa-calendar-times"></i>

                <h3>
                  No available times
                </h3>

                <p>
                  Please select another date.
                </p>

              </div>

            ) : (

              <>
                <style>{`
                  .microsoft-booking-busy-slot:hover .microsoft-booking-busy-tooltip {
                    opacity: 1 !important;
                  }
                `}</style>

                <div
                  style={{
                    display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fill, minmax(125px, 1fr))',
                  gap: '8px'
                }}
              >

                {calendarSlots.map((slot, index) => {
                  const selected =
                    selectedSlot?.startUtc === slot.startUtc &&
                    selectedSlot?.endUtc === slot.endUtc;
                  const busy = slot.status === 'busy';

                  return (
                    <button
                      type="button"
                      key={`${slot.startUtc}-${slot.endUtc}-${index}`}
                      className={`microsoft-booking-busy-slot ${
                        selected
                          ? 'btn btn-primary'
                          : busy
                            ? 'btn btn-danger'
                            : 'btn btn-success'
                      }`}
                      onClick={() => {
                        if (!busy) setSelectedSlot(slot);
                      }}
                      disabled={booking || busy}
                      title={busy ? 'Busy - hover for meeting details' : 'Available - click to select'}
                      style={{
                        minHeight: '42px',
                        opacity: busy ? 0.75 : 1,
                        position: 'relative'
                      }}
                    >
                      {formatDisplayTime(getTimePart(slot.userStart || slot.start))}
                      {busy ? ' - Busy' : ''}
                      {busy && (
                        <span
                          style={{
                            position: 'absolute',
                            left: '50%',
                            bottom: 'calc(100% + 8px)',
                            transform: 'translateX(-50%)',
                            width: '250px',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            background: '#222',
                            color: '#fff',
                            textAlign: 'left',
                            fontSize: '12px',
                            lineHeight: '1.45',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.15s ease',
                            zIndex: 20
                          }}
                          className="microsoft-booking-busy-tooltip"
                        >
                          <strong style={{ display: 'block', marginBottom: '4px' }}>
                            {slot.subject || slot.serviceName || 'Busy'}
                          </strong>
                          <div>
                            {formatDisplayTime(getTimePart(slot.userStart || slot.start))}
                            {' - '}
                            {formatDisplayTime(getTimePart(slot.userEnd || slot.end))}
                          </div>
                          {slot.serviceName && slot.serviceName !== slot.subject && (
                            <div>Service: {slot.serviceName}</div>
                          )}
                          {slot.customerName && (
                            <div>Customer: {slot.customerName}</div>
                          )}
                          {slot.staffName && (
                            <div>Staff: {slot.staffName}</div>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })}

                </div>
              </>
            )}

          </div>

          <div className="form-group">

            <label>
              Notes
            </label>

            <textarea
              className="form-control"
              rows="4"
              value={notes}
              onChange={(event) =>
                setNotes(
                  event.target.value
                )
              }
              placeholder="Optional notes for the demo..."
              disabled={booking}
            />

          </div>

          {selectedSlot && (
            <div
              style={{
                background: '#edf6f1',
                border: '1px solid #d6e8de',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '15px'
              }}
            >

              <strong>
                Selected:
              </strong>

              {' '}

              {formatDisplayDate(
                selectedSlot.userStart ||
                selectedDate
              )}

              {' at '}

              {formatDisplayTime(
                getTimePart(
                  selectedSlot.userStart ||
                  selectedSlot.start
                )
              )}

            </div>
          )}

          <div className="form-actions">

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                navigate(
                  `/leads`
                )
              }
              disabled={booking}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleBooking}
              disabled={
                booking ||
                loadingSlots ||
                !selectedSlot
              }
            >

              {booking ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {' '}
                  Booking...
                </>
              ) : (
                <>
                  <i className="fas fa-calendar-check"></i>
                  {' '}
                  Book Demo
                </>
              )}

            </button>

          </div>

        </div>

      </div>

    </Layout>
  );
}

export default BookDemo;